import { FocusableOption, LiveAnnouncer } from '@angular/cdk/a11y'
import {
    BlockScrollStrategy,
    ConnectedPosition,
    ScrollStrategy,
    ViewportRuler,
} from '@angular/cdk/overlay'

import {
    AfterContentInit,
    Component,
    ContentChildren,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    QueryList,
    Self,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    DOCUMENT,
    ChangeDetectionStrategy,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { SelectOptGroupDirective } from '@app-domains/ui/components/select/optgroup/select-optgroup.directive'
import { SelectOptionDirective } from '@app-domains/ui/components/select/option/select-option.directive'
import { OptionConfig, OptionGroupConfig, Select } from '@app-domains/ui/components/select/select.component.types'
import { SelectControlRegistryService } from '@app-domains/ui/services/select-control-registry/select-control-registry.service'
import { indexWithinParent } from '@app-lib/common.lib'
import { flatten, map, pipe, prop, sortBy } from 'ramda'
import { combineLatest, Observable, startWith, takeWhile } from 'rxjs'
import { map as rxMap } from 'rxjs/operators'

/**
 * # Select component
 *
 * Renders a select with a label.
 *
 * ## Input bindings
 *
 * | Input binding                                    | Default        | Required |
 * | ------------------------------------------------ | -------------- | -------- |
 * | {@link SelectComponent.control [control]}      | `undefined`    | true       |
 * | {@link SelectComponent.disabled [disabled]}    | `false`        | false      |
 *
 * -
 *
 * ## Option content
 * The options are projected in an `<ng-content>` slot:
 *
 * ```html
 * <app-select [control]="...">
 *     <option value="option-1">Option 1</option>
 * </app-select>
 * ```
 */
@Component({
    selector: 'app-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    viewProviders: [SelectControlRegistryService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SelectComponent<T = unknown> implements OnChanges, AfterContentInit, FocusableOption {
    @Input()
    public size: Select.Size = 'normal'

    @Input()
    public placeholder: string

    @ContentChildren(SelectOptionDirective)
    public optionsList: QueryList<SelectOptionDirective>

    @ContentChildren(SelectOptGroupDirective)
    public optgroupsList: QueryList<SelectOptGroupDirective>

    @ViewChild('container')
    public container: ElementRef<HTMLDivElement>

    @ViewChild('selectElement')
    public selectElement: ElementRef<HTMLSelectElement>

    public overlayConfigs$: Observable<(OptionGroupConfig | OptionConfig)[]>

    @Input()
    public overlayPositions?: ConnectedPosition[]

    /**
     * Requires a form control to attach to the native input element. This input accepts
     * `undefined` so that the existence check for controls within form-groups can be done
     * internally. If `undefined` is bound then the select will simply not render.
     */
    @Input()
    public control: FormControl

    public defaultSelected$: Observable<boolean>
    public scrollStrategy: ScrollStrategy

    constructor(
        @Self()
        public selectControlRegistry: SelectControlRegistryService<T>,
        private liveAnnouncer: LiveAnnouncer,
        private readonly viewportRuler: ViewportRuler,
        @Inject(DOCUMENT) protected readonly document: Document,
    ) {
        this.scrollStrategy = new BlockScrollStrategy(viewportRuler, document)
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.control) {
            const control = changes.control.currentValue
            this.defaultSelected$ = control.valueChanges.pipe(
                rxMap(() => false),
                startWith(control.value === null),
                takeWhile(v => v !== false, true),
            )
        }
    }

    public ngAfterContentInit(): void {
        this.selectControlRegistry.initialise(this)
        const parseOptions = (options: QueryList<SelectOptionDirective>): OptionConfig[] => pipe(
            map((option: SelectOptionDirective) => {
                const imageElement = option.el.nativeElement.firstElementChild as HTMLImageElement
                const optionIsImage = imageElement && imageElement.tagName === 'IMG'

                const image = optionIsImage
                    ? imageElement.src
                    : undefined

                return ({
                    index: indexWithinParent(option.el.nativeElement),
                    value: option.value,
                    label: option.el.nativeElement.innerText,
                    image,
                    disabled: option.el.nativeElement.disabled,
                })
            }),
            sortBy(prop('index')),
        )([...options])

        const parseOptGroups = (groups: QueryList<SelectOptGroupDirective>): OptionGroupConfig[] => pipe(
            map((group: SelectOptGroupDirective) => ({
                index: indexWithinParent(group.el.nativeElement),
                label: group.el.nativeElement.label,
                options: parseOptions(group.options),
            })),
            sortBy(prop('index')),
        )([...groups])

        this.overlayConfigs$ = combineLatest([
            this.optionsList.changes.pipe(
                startWith(this.optionsList),
                rxMap(parseOptions),
            ),
            this.optgroupsList.changes.pipe(
                startWith(this.optgroupsList),
                rxMap(parseOptGroups),
            ),
        ]).pipe(
            rxMap(([options, groups]) => sortBy(prop('index'))([...options, ...groups])),
        )
    }

    public focus() {
        this.container.nativeElement.focus()
    }

    public onMouseDown($event: Event) {
        $event.preventDefault()
        $event.stopImmediatePropagation()
        this.selectElement.nativeElement.focus()
        this.selectControlRegistry.open()
    }

    /**
     * @internal
     *  This is used to determine if the select is focused.
     * @returns {Promise<void>}
     */
    public async onFocus() {
        const count = this.optionsList.length + this.optgroupsList.reduce((acc, val) => {
            return acc + val.options.length
        }, 0)
        const options: SelectOptionDirective[] = [
            ...this.optionsList,
            ...flatten(this.optgroupsList.map((g) => [...g.options])),
        ]
        if (this.control.value) {
            const index = options.findIndex((o) => o.value === this.control.value)
            await this.liveAnnouncer.announce(`${this.control.value}, combo box ${index + 1} of ${count}`)
        } else {
            await this.liveAnnouncer.announce(`combo box 0 of ${count}`)
        }
    }
}
